import type { QuizDefinition } from "./models/QuizModel";

export type SaveDraftPayload = {
    title: string;
    intro_text: string | null;
    slug: string;
    definition: QuizDefinition;
    expected_revision: number;
};

export type SaveDraftResponse = {
    id: number;
    draft_revision: number;
};

export type PublishResponse = {
    draft_revision: number;
    id: number;
    status: string;
    published_version_number: number;
};

export type QuizIssue = {
    code: string;
    message: string;
    question_id: string | null;
    option_id: string | null;
    result_id: string | null;
};

export class QuizApiError extends Error {
    status: number;
    code: string | null;
    issues: QuizIssue[];

    constructor(status: number, message: string, code: string | null, issues: QuizIssue[]) {
        super(message);
        this.status = status;
        this.code = code;
        this.issues = issues;
    }
}

const csrfToken = (): string => document.querySelector(`meta[name="csrf-token"]`)?.getAttribute(`content`) || ``;

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
        "X-CSRF-TOKEN": csrfToken(),
        Accept: `application/json`,
    };

    let requestBody: string | undefined;

    if (body !== undefined) {
        headers[`Content-Type`] = `application/json`;
        requestBody = JSON.stringify(body);
    }

    const response = await fetch(url, { method, headers, body: requestBody });
    const json = await response.json().catch(() => ({}));

    if (response.ok) {
        return json.payload as T;
    }

    throw new QuizApiError(
        response.status,
        json.description || `Ошибка`,
        json.payload?.code ?? null,
        json.payload?.issues ?? [],
    );
}

export const saveDraft = async (id: number, payload: SaveDraftPayload): Promise<SaveDraftResponse> => {
    return await call<SaveDraftResponse>(`PUT`, `/admin/quizzes/${id}`, payload);
};

export const publishQuiz = async (id: number, expectedRevision: number): Promise<PublishResponse> => {
    return await call<PublishResponse>(`POST`, `/admin/quizzes/${id}/publish`, { expected_revision: expectedRevision });
};

export const archiveQuiz = async (id: number): Promise<{ id: number; status: string }> => {
    return await call(`POST`, `/admin/quizzes/${id}/archive`);
};

export const deleteQuiz = async (id: number): Promise<{ deleted: boolean }> => {
    return await call(`DELETE`, `/admin/quizzes/${id}`);
};
